import { v4 as uuidv4 } from 'uuid';

import { EntityComponent, EntityType } from 'contexts/datamodel/DataModelContext';
import {
  GroupConfig,
  ModuleContainerUnion,
  MultiZoneModuleAttributes,
  PageDto,
  StructureGroupConfig,
  TreatmentTypeSetting
} from 'data/generated/graphql';
import { convertPageModuleItemsToDataModelEntities } from './allesseh';
import { MULTIZONE_MODULE } from './constants';

export type RegionGroupConfig = GroupConfig | (StructureGroupConfig & { id: string });

export interface RegionToggleConfig {
  groupConfig: RegionGroupConfig;
  treatmentTypeSettings: TreatmentTypeSetting[];
  pageDTO: PageDto;
}

/**
 * Creates a default module for a region based on its GroupConfig and TreatmentTypeSettings
 */
export const createDefaultModuleForRegion = (
  groupConfig: RegionGroupConfig,
  treatmentTypeSettings: TreatmentTypeSetting[]
): ModuleContainerUnion | null => {
  // Uses defaultTreatmentType if set, otherwise falls back to the first allowedTreatmentType.
  // This creates a brand-new empty module — the group's previous modules are not restored.
  // TODO: consider persisting the previous module state so toggling back on restores the original order.
  const treatmentType: string | null | undefined =
    groupConfig.defaultTreatmentType ?? groupConfig.allowedTreatmentTypes[0];

  if (!treatmentType) {
    return null;
  }

  // Find the treatment type setting for this treatment type
  const treatmentSetting = treatmentTypeSettings.find(
    (t) => t.treatmentType === treatmentType || t.treatmentTypeKey === treatmentType
  );

  if (!treatmentSetting?.module) {
    return null;
  }

  const defaultModule = treatmentSetting.module;
  const moduleSubType = treatmentSetting.subtype;

  // Convert module items to collection entities using the shared utility
  const collection = convertPageModuleItemsToDataModelEntities(defaultModule);

  const attributes = {
    pageModule: defaultModule,
    brand: treatmentSetting.brand,
    moduleId: defaultModule.moduleId,
    disableModuleIdEdit: defaultModule.disableModuleIdEdit
  } as MultiZoneModuleAttributes;

  const newModule: ModuleContainerUnion = {
    type: 'Module',
    attributes,
    collection,
    groupId: groupConfig.id,
    isGroupEnabled: true,
    uuid: uuidv4()
  };

  if (moduleSubType === MULTIZONE_MODULE) {
    newModule.subtype = MULTIZONE_MODULE;
    newModule.attributes.zones = treatmentSetting.zones;
  }

  return newModule;
};

/**
 * Handles enabling/disabling a region (group) on a page
 *
 * When enabled: Creates a module for the region but with an empty collection array
 * When disabled: Removes all modules with this groupId
 */
export const handleRegionToggle = (
  enabled: boolean,
  config: RegionToggleConfig,
  insertEntity: (hierarchyId: string, entity: EntityComponent) => void,
  removeEntity: (hierarchyId: string) => void,
  traverseEntity: (entity: EntityComponent, callback: (entity: EntityComponent, hierarchyId: string) => void) => void
) => {
  const { groupConfig, treatmentTypeSettings, pageDTO } = config;

  if (enabled) {
    // Create a module for this region with an empty collection
    // Users can add content to it later
    const pageCollection = pageDTO.root.collection[0]?.collection;
    const insertIndex = pageCollection.length || 0;
    const hierarchyId = `0-0-${insertIndex}`;

    const newModule = createDefaultModuleForRegion(groupConfig, treatmentTypeSettings);

    if (newModule) {
      // Clear the collection so the module starts with no content
      newModule.collection = [];
      insertEntity(hierarchyId, newModule as EntityComponent);
    }
  } else {
    // Remove all modules with this groupId
    const entityHierarchyIds: string[] = [];

    traverseEntity(pageDTO.root, (entity, hierarchyId) => {
      if (entity.type === EntityType.Module && entity.groupId === groupConfig.id) {
        entityHierarchyIds.push(hierarchyId);
      }
    });

    // Remove in reverse order to maintain hierarchy indices
    entityHierarchyIds.reverse().forEach((hierarchyId) => removeEntity(hierarchyId));
  }
};
